import React, { useState, useEffect } from 'react';
import { 
  Globe, Phone, MapPin, Mail, Menu, X, 
  ChevronDown, Scale, Users, 
  MessageCircle, Star, ArrowRight, CheckCircle2, Loader2
} from 'lucide-react';

import emailjs from "@emailjs/browser";


// --- DICTIONARY & CONTENT ---
const content = {
  en: {
    nav: { about: "About Us", services: "Services", whyUs: "Why Choose Us", contact: "Contact", bookBtn: "Book Consultation", lang: "EN / VI" },
    hero: {
      tag: "TRIỆU LEGAL & IMMIGRATION SERVICES",
      headline: "Trusted Legal & Immigration Solutions for Individuals, Families, and Businesses",
      subhead: "Professional, personalized legal guidance with integrity, clarity, and dedication.",
      btnPrimary: "Book Consultation", btnSecondary: "Contact Us"
    },
    about: {
      title: "About Us",
      body: "At Triệu Legal & Immigration Services, we provide expert legal representation and dedicated immigration support. Our mission is to navigate the complexities of the legal system with professionalism and an unwavering commitment to your success."
    },
    services: {
      title: "Our Expertise",
      categories: [
        { title: "Immigration & Residency Services", items: ["Family-Based Immigration", "Fiancé(e) Visa (K-1)", "Marriage-Based Green Card", "Adjustment of Status", "Employment-Based Green Cards", "Investor & Entrepreneur Immigration", "Naturalization & Citizenship", "Asylum & VAWA Support", "Removal Defense"] },
        { title: "Civil Documentation & Vital Records", items: ["Birth Certificates", "Marriage Registration", "Divorce Records", "Death Certificates", "Document Notarization", "Apostille Services", "Power of Attorney"] },
        { title: "Legal & Litigation Support", items: ["Contract & Business Disputes", "Debt Collection", "Property Disputes", "Child Custody & Support", "Business Formation", "Contract Drafting", "Court Filing"] }
      ]
    },
    whyUs: {
      title: "Why Choose Us",
      cards: [
        { icon: 'Users', title: "Experienced Team", desc: "Precision and expertise in every case." },
        { icon: 'Scale', title: "Personalized Strategy", desc: "Tailored legal solutions for you." },
        { icon: 'MessageCircle', title: "Transparent Communication", desc: "Keeping you informed always." },
        { icon: 'Star', title: "Commitment to Results", desc: "Driven by professionalism and integrity." }
      ]
    },
    contact: {
      title: "Get In Touch",
      form: { name: "Full Name", email: "Email Address", phone: "Phone Number", service: "Service Needed", message: "Your Message", submit: "Send Message", sending: "Sending...", success: "Message Sent Successfully!", error: "Something went wrong, please try again." },
      infoTitle: "Contact Information"
    },
    footer: { mission: "Expert legal and immigration support with professionalism.", quickLinks: "Quick Links", rights: "© 2026 Triệu Legal & Immigration Services. All Rights Reserved.", legal: "Legal Disclaimer", privacy: "Privacy Policy" },
    modals: { close: "Close" }
  },
  vi: {
    nav: { about: "Giới Thiệu", services: "Dịch Vụ", whyUs: "Cam Kết", contact: "Liên Hệ", bookBtn: "Đặt Lịch Tư Vấn", lang: "VI / EN" },
    hero: {
      tag: "TRIỆU LEGAL & IMMIGRATION SERVICES",
      headline: "Giải Pháp Pháp Lý & Di Trú Đáng Tin Cậy Cho Cá Nhân, Gia Đình Và Doanh Nghiệp",
      subhead: "Hướng dẫn pháp lý chuyên nghiệp, tận tâm với sự chính trực, rõ ràng và cống hiến.",
      btnPrimary: "Đặt Lịch Tư Vấn", btnSecondary: "Liên Hệ Với Chúng Tôi"
    },
    about: {
      title: "Giới Thiệu",
      body: "Tại Triệu Legal & Immigration Services, chúng tôi cung cấp dịch vụ đại diện pháp lý chuyên môn và hỗ trợ di trú tận tâm. Sứ mệnh của chúng tôi là giúp bạn vượt qua những hệ thống pháp lý phức tạp với sự chuyên nghiệp và cam kết vững chắc vì sự thành công của bạn."
    },
    services: {
      title: "Lĩnh Vực Chuyên Môn",
      categories: [
        { title: "Dịch Vụ Di Trú & Cư Trú", items: ["Bảo Lãnh Thân Nhân", "Visa Hôn Phu / Hôn Thê", "Thẻ Xanh Kết Hôn", "Chuyển Diện Cư Trú", "Thẻ Xanh Làm Việc", "Di Trú Nhà Đầu Tư", "Thi Quốc Tịch", "Hồ Sơ Tị Nạn", "Chống Trục Xuất"] },
        { title: "Giấy Tờ Hộ Tịch & Dân Sự", items: ["Giấy Khai Sinh", "Đăng Ký Kết Hôn", "Hồ Sơ Ly Hôn", "Giấy Chứng Tử", "Công Chứng Giấy Tờ", "Hợp Pháp Hóa Lãnh Sự", "Giấy Ủy Quyền"] },
        { title: "Hỗ Trợ Pháp Lý & Tố Tụng", items: ["Tranh Chấp Hợp Đồng", "Thu Hồi Nợ", "Tranh Chấp Tài Sản", "Quyền Nuôi Con", "Thành Lập Doanh Nghiệp", "Soạn Thảo Hợp Đồng", "Hồ Sơ Khởi Kiện"] }
      ]
    },
    whyUs: {
      title: "Cam Kết Của Chúng Tôi",
      cards: [
        { icon: 'Users', title: "Đội Ngũ Giàu Kinh Nghiệm", desc: "Chuyên môn và sự chuẩn xác trong từng hồ sơ." },
        { icon: 'Scale', title: "Chiến Lược Cá Nhân Hóa", desc: "Giải pháp thiết kế riêng cho bạn." },
        { icon: 'MessageCircle', title: "Giao Tiếp Minh Bạch", desc: "Cập nhật thông tin cho bạn liên tục." },
        { icon: 'Star', title: "Cam Kết Kết Quả", desc: "Thúc đẩy bởi sự chính trực và thành công." }
      ]
    },
    contact: {
      title: "Liên Hệ Với Chúng Tôi",
      form: { name: "Họ và Tên", email: "Địa chỉ Email", phone: "Số Điện Thoại", service: "Dịch Vụ Cần Hỗ Trợ", message: "Lời Nhắn Của Bạn", submit: "Gửi Tin Nhắn", sending: "Đang gửi...", success: "Gửi tin nhắn thành công!", error: "Có lỗi xảy ra, vui lòng thử lại." },
      infoTitle: "Thông Tin Liên Hệ"
    },
    footer: { mission: "Đại diện pháp lý và di trú tận tâm, chuyên nghiệp.", quickLinks: "Liên Kết Nhanh", rights: "© 2026 Triệu Legal & Immigration Services. Bảo Lưu Mọi Quyền.", legal: "Tuyên Bố Pháp Lý", privacy: "Chính Sách Bảo Mật" },
    modals: { close: "Đóng" }
  }
};

const IconMap = { Users: Users, Scale: Scale, MessageCircle: MessageCircle, Star: Star };

export default function App() {
  const [lang, setLang] = useState('en');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(0);
  const [activeModal, setActiveModal] = useState(null);
  
  // Form State
  const [formStatus, setFormStatus] = useState('idle'); // idle, sending, success, error

  const t = content[lang];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLang = () => setLang(prev => prev === 'en' ? 'vi' : 'en');
  
  const scrollTo = (id) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // --- SUBMIT FUNCTION ---

  const sendEmail = (e) => {
    e.preventDefault();
    setFormStatus('sending');
    emailjs.send(
      "service_hb5o4po",
      "template_q3siye2",
      {
        title: e.target.title?.value,
        name: e.target.name.value,
        email: e.target.email.value,
        phone: e.target.phone?.value,
        service: e.target.service?.value,
        message: e.target.message.value,
      },
      "UH0YPJm7AikPGA_4H"
    )
    .then(() => {
      setFormStatus('success');
          e.target.reset();
          setTimeout(() => setFormStatus('idle'), 5000);
    })
    .catch(() => {
      setFormStatus('error');
    });
  };

  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    const formData = new FormData(e.target);
    
    // THAY THẾ 'YOUR_ACCESS_KEY_HERE' BẰNG MÃ TỪ WEB3FORMS
    formData.append("access_key", "8fc9f1fc-d74a-4606-a249-559603760743");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setFormStatus('success');
        e.target.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen font-sans text-[#2C3E50] bg-[#FFFFFF] antialiased">
      <style dangerouslySetInnerHTML={{__html: `@import url('https://fonts.googleapis.com/css2?family=Noto+Sans:wght@300;400;500;600;700&family=Playfair+Display:wght@400;500;600;700&display=swap'); .font-serif { font-family: 'Playfair Display', serif; } .font-sans { font-family: 'Noto Sans', sans-serif; }`}} />

      {/* NAVIGATION */}
      <nav className={`fixed w-full z-50 transition-all duration-300 top-0 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-white/95 shadow-sm py-3'}`}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex justify-between items-center">
          <div className="flex items-center cursor-pointer flex-shrink-0" onClick={() => window.scrollTo(0,0)}>
            <img src="/Tron.jpg" alt="Logo" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-contain drop-shadow-sm" />
          </div>

          <div className="hidden lg:flex items-center space-x-8">
            <button onClick={() => scrollTo('about')} className="font-semibold text-[15px] hover:text-[#D4AF37] text-[#0F2040] transition-colors">{t.nav.about}</button>
            <button onClick={() => scrollTo('services')} className="font-semibold text-[15px] hover:text-[#D4AF37] text-[#0F2040] flex items-center">{t.nav.services} <ChevronDown size={14} className="ml-1"/></button>
            <button onClick={() => scrollTo('why-us')} className="font-semibold text-[15px] hover:text-[#D4AF37] text-[#0F2040] transition-colors">{t.nav.whyUs}</button>
          </div>

          <div className="hidden lg:flex items-center space-x-6">
            <div className="flex flex-col items-end border-r border-gray-200 pr-6">
              <span className="text-[13px] text-gray-500 flex items-center mb-1"><MapPin size={14} className="text-[#D4AF37] mr-1.5"/> 4238 El Cajon Blvd, San Diego</span>
              <span className="text-[15px] font-bold text-[#0F2040] flex items-center"><Phone size={14} className="text-[#D4AF37] mr-1.5"/> 619-528-0888</span>
            </div>
            <button onClick={toggleLang} className="flex items-center font-bold text-[15px] bg-gray-50 px-4 py-2 rounded-sm border border-gray-200 shadow-sm transition-all hover:bg-gray-100"><Globe size={18} className="mr-2"/> {t.nav.lang}</button>
          </div>

          <div className="lg:hidden flex items-center">
            <button onClick={toggleLang} className="mr-4 text-[#0F2040] font-bold text-sm bg-gray-50 px-3 py-1.5 rounded-sm border border-gray-200">{t.nav.lang}</button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-[#0F2040]"><Menu size={28} /></button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-40 pb-20 md:pt-56 md:pb-32 flex items-center min-h-[85vh] bg-[#0F2040]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10 w-full">
          <div className="max-w-3xl">
            <div className="inline-block px-3 py-1 bg-[#D4AF37]/20 border border-[#D4AF37]/50 text-[#D4AF37] rounded-sm text-xs font-bold tracking-widest mb-6">{t.hero.tag}</div>
            <h1 className="font-serif text-4xl md:text-6xl text-white font-bold mb-6 leading-tight">{t.hero.headline}</h1>
            <p className="text-lg md:text-xl text-gray-200 mb-10 font-light leading-relaxed">{t.hero.subhead}</p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <button onClick={() => scrollTo('contact')} className="bg-[#D4AF37] text-[#0F2040] px-8 py-4 rounded-sm font-bold text-lg hover:bg-[#c29e31] transition-all shadow-lg text-center flex items-center justify-center">{t.hero.btnPrimary} <ArrowRight size={18} className="ml-2" /></button>
              <button onClick={() => scrollTo('contact')} className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-sm font-bold text-lg hover:bg-white hover:text-[#0F2040] transition-all text-center">{t.hero.btnSecondary}</button>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT US */}
      <section id="about" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col lg:flex-row items-center gap-16">
          <div className="w-full lg:w-1/2 relative">
            <div className="absolute top-4 -left-4 w-full h-full border-2 border-[#D4AF37] hidden md:block"></div>
            <img src="e1e025ec-3b01-4f8a-b944-3d2547d8c493.jfif" alt="Lawyer" className="relative z-10 w-full h-[500px] object-cover shadow-2xl rounded-sm" />
          </div>
          <div className="w-full lg:w-1/2">
            <h2 className="font-serif text-4xl text-[#0F2040] font-bold mb-8 relative inline-block">{t.about.title} <span className="absolute -bottom-2 left-0 w-20 h-1 bg-[#D4AF37]"></span></h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">{t.about.body}</p>
            <div className="flex items-center space-x-6 pt-6 border-t border-gray-100">
              <div className="w-16 h-16 bg-[#0F2040] flex items-center justify-center rounded-full text-[#D4AF37]"><Scale size={32} /></div>
              <div><h4 className="font-bold text-[#0F2040] text-xl">San Diego, CA</h4><p className="text-gray-500 text-sm">Official Headquarters</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16"><h2 className="font-serif text-4xl text-[#0F2040] font-bold mb-4">{t.services.title}</h2><div className="w-24 h-1 bg-[#D4AF37] mx-auto"></div></div>
          <div className="space-y-4">
            {t.services.categories.map((cat, i) => (
              <div key={i} className="bg-white border rounded-sm shadow-sm">
                <button onClick={() => setOpenAccordion(openAccordion === i ? null : i)} className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-gray-50">
                  <span className={`font-serif font-bold text-xl md:text-2xl ${openAccordion === i ? 'text-[#D4AF37]' : 'text-[#0F2040]'}`}>{cat.title}</span>
                  <div className={`transition-transform duration-300 ${openAccordion === i ? 'rotate-180 text-[#D4AF37]' : 'text-[#0F2040]'}`}><ChevronDown size={24} /></div>
                </button>
                {openAccordion === i && (
                  <div className="px-6 pb-6 pt-2 border-t border-gray-100 animate-in slide-in-from-top duration-300">
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {cat.items.map((item, idx) => (
                        <li key={idx} className="flex items-start text-gray-700"><CheckCircle2 className="text-[#D4AF37] mr-3 mt-1 flex-shrink-0" size={18} /> {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section id="why-us" className="py-20 md:py-32 bg-[#0F2040] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16"><h2 className="font-serif text-4xl font-bold mb-4">{t.whyUs.title}</h2><div className="w-24 h-1 bg-[#D4AF37] mx-auto"></div></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.whyUs.cards.map((card, i) => {
              const Icon = IconMap[card.icon];
              return (
                <div key={i} className="bg-white p-8 rounded-sm text-center shadow-xl transition-transform hover:-translate-y-2">
                  <div className="w-16 h-16 bg-[#0F2040]/5 rounded-full flex items-center justify-center mx-auto mb-6"><Icon size={32} className="text-[#D4AF37]" /></div>
                  <h3 className="font-serif text-xl font-bold text-[#0F2040] mb-4">{card.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{card.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT (MESSAGE SUBMIT INTEGRATED) */}
      <section id="contact" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="bg-white p-8 md:p-12 rounded-sm shadow-2xl border border-gray-100">
            <h2 className="font-serif text-3xl text-[#0F2040] font-bold mb-8">{t.contact.title}</h2>
            
            {/* FORM SUBMISSION LOGIC */}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input type="text" name="name" required placeholder={t.contact.form.name} className="w-full px-4 py-3 bg-gray-50 border rounded-sm focus:ring-2 focus:ring-[#D4AF37] outline-none transition-all" />
                <input type="tel" name="phone" placeholder={t.contact.form.phone} className="w-full px-4 py-3 bg-gray-50 border rounded-sm focus:ring-2 focus:ring-[#D4AF37] outline-none transition-all" />
              </div>
              <input type="email" name="email" required placeholder={t.contact.form.email} className="w-full px-4 py-3 bg-gray-50 border rounded-sm focus:ring-2 focus:ring-[#D4AF37] outline-none transition-all" />
              <select name="service" required className="w-full px-4 py-3 bg-gray-50 border rounded-sm focus:ring-2 focus:ring-[#D4AF37] outline-none transition-all text-gray-500">
                <option value="">{t.contact.form.selectService}</option>
                {t.services.categories.map((c, i) => <option key={i} value={c.title}>{c.title}</option>)}
              </select>
              <textarea name="message" required rows="4" placeholder={t.contact.form.message} className="w-full px-4 py-3 bg-gray-50 border rounded-sm focus:ring-2 focus:ring-[#D4AF37] outline-none transition-all"></textarea>
              
              <button 
                type="submit" 
                disabled={formStatus === 'sending'}
                className="w-full bg-[#0F2040] text-white py-4 rounded-sm font-bold text-lg hover:bg-gray-900 shadow-lg flex items-center justify-center disabled:bg-gray-400"
              >
                {formStatus === 'sending' ? <><Loader2 className="animate-spin mr-2" /> {t.contact.form.sending}</> : t.contact.form.submit}
              </button>

              {/* Status Notifications */}
              {formStatus === 'success' && <div className="text-green-600 bg-green-50 p-4 rounded-sm border border-green-200 text-center font-bold animate-in fade-in">{t.contact.form.success}</div>}
              {formStatus === 'error' && <div className="text-red-600 bg-red-50 p-4 rounded-sm border border-red-200 text-center font-bold animate-in fade-in">{t.contact.form.error}</div>}
            </form>
          </div>

          <div className="flex flex-col justify-between">
            <div>
              <h2 className="font-serif text-3xl text-[#0F2040] font-bold mb-8">{t.contact.infoTitle}</h2>
              <div className="space-y-8 mb-12">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#0F2040]/5 rounded-full flex items-center justify-center mr-6"><MapPin className="text-[#D4AF37]" size={24} /></div>
                  <div><h4 className="text-[#0F2040] font-bold text-lg mb-1">Office Location</h4><p className="text-gray-600">4238 El Cajon Blvd, San Diego, CA 92105</p></div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#0F2040]/5 rounded-full flex items-center justify-center mr-6"><Phone className="text-[#D4AF37]" size={24} /></div>
                  <div><h4 className="text-[#0F2040] font-bold text-lg mb-1">Phone Number</h4><p className="text-gray-600 font-medium">619-528-0888</p></div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#0F2040]/5 rounded-full flex items-center justify-center mr-6"><Mail className="text-[#D4AF37]" size={24} /></div>
                  <div><h4 className="text-[#0F2040] font-bold text-lg mb-1">Email Address</h4><p className="text-gray-600 font-medium break-all">admin@trieulegalimmigrationservices.com</p></div>
                </div>
              </div>
            </div>
            <div className="w-full h-64 bg-gray-200 rounded-sm shadow-inner relative overflow-hidden">
              <iframe title="Map" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3355.6766479815343!2d-117.09886402434407!3d32.74838637367503!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80d956a65cb6f4cd%3A0x8670ab3dfecad14d!2s4238%20El%20Cajon%20Blvd%2C%20San%20Diego%2C%20CA%2092105!5e0!3m2!1sen!2sus!4v1714000000000!5m2!1sen!2sus" width="100%" height="100%" className="absolute inset-0 grayscale border-0"></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0F2040] text-white pt-20 pb-10 border-t-4 border-[#D4AF37]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div className="lg:col-span-2">
            <div className="bg-white p-1 rounded-full inline-block mb-6 shadow-xl"><img src="Logo Tròn.jpg" alt="Logo" className="w-24 h-24 rounded-full object-contain" /></div>
            <p className="text-gray-400 max-w-sm mb-6 leading-relaxed">{t.footer.mission}</p>
          </div>
          <div><h4 className="font-bold text-lg mb-6 border-b border-white/10 pb-4">{t.footer.quickLinks}</h4>
            <ul className="space-y-4 text-gray-400">
              {['about', 'services', 'why-us', 'contact'].map((item) => (
                <li key={item}><button onClick={() => scrollTo(item)} className="hover:text-[#D4AF37] transition-colors">{t.nav[item]}</button></li>
              ))}
            </ul>
          </div>
          <div><h4 className="font-bold text-lg mb-6 border-b border-white/10 pb-4">{t.contact.infoTitle}</h4>
            <ul className="space-y-4 text-gray-400 text-sm">
              <li className="flex items-start"><MapPin size={16} className="text-[#D4AF37] mr-3 mt-1"/> 4238 El Cajon Blvd, San Diego, CA 92105</li>
              <li className="flex items-center"><Phone size={16} className="text-[#D4AF37] mr-3"/> 619-528-0888</li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between text-sm text-gray-500 items-center">
          <p>{t.footer.rights}</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <button className="hover:text-white transition-colors">{t.footer.legal}</button>
            <button className="hover:text-white transition-colors">{t.footer.privacy}</button>
          </div>
        </div>
      </footer>
    </div>
  );
}